import { ApiResponse } from "./ApiResponse";
import {
  AUTH_SESSION_ERROR,
  invalidateAuthSession,
  isWithinPostLoginGracePeriod,
} from "../utils/auth";
import { API_ROOT } from "./ApiUrls";
import { encryptionService } from "../utils/encryption";

/**
 * Base API service with common functionality for all services
 * Provides reusable methods for API operations, logging, and error handling
 */
/** Tracks which JWT was sent per response so stale 401s after re-login do not clear the new session. */
const responseAuthToken = new WeakMap<Response, string | null>();

export class BaseApiService {
  // ==================== LOGGING UTILITIES ====================

  /**
   * Logs API operation with consistent formatting
   * @param operation - Operation being performed
   * @param data - Data to log (optional)
   */
  protected static logOperation(operation: string, data?: any): void {
    const timestamp = new Date().toISOString();
    if (data) {
      console.log(`[${timestamp}] ${operation}:`, data);
    } else {
      console.log(`[${timestamp}] ${operation}`);
    }
  }

  /**
   * Logs API errors with consistent formatting
   * @param operation - Operation that failed
   * @param error - Error object or message
   */
  protected static logError(operation: string, error: any): void {
    const timestamp = new Date().toISOString();
    console.error(`[${timestamp}] ❌ ${operation}:`, error);
  }

  // ==================== QUERY PARAMETER UTILITIES ====================

  /**
   * Builds query parameters string from filters object
   * @param filters - Filter object to convert to query parameters
   * @returns URLSearchParams object
   */
  protected static buildQueryParams(
    filters: Record<string, any>
  ): URLSearchParams {
    const queryParams = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== "" && value !== null) {
        if (Array.isArray(value)) {
          // Handle array filters (categories, locations, etc.)
          value.forEach((item) => {
            if (item) {
              queryParams.append(key, item.toString());
            }
          });
        } else {
          queryParams.append(key, value.toString());
        }
      }
    });

    return queryParams;
  }

  // ==================== PRIVATE UTILITY METHODS ====================

  /**
   * Private method to ensure URL has proper base URL
   * @param url - URL to process
   * @returns Full URL with base URL if needed
   */
  private static ensureFullUrl(url: string): string {
    if (/^https?:\/\//i.test(url)) {
      return url;
    }
    const path = url.startsWith("/") ? url : `/${url}`;
    const root = API_ROOT.replace(/\/+$/, "");
    return `${root}${path}`;
  }

  /**
   * Private method to get authentication headers
   * @returns Headers object with authentication token
   */
  private static getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }

    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * Auth headers for multipart uploads — omit Content-Type so the browser sets the boundary.
   */
  private static getAuthHeadersMultipart(): Record<string, string> {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("No authentication token found");
    }
    return {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  /**
   * Check if request should be encrypted
   */
  private static shouldEncryptRequest(url: string): boolean {
    // For now, disable request encryption to avoid 500 errors
    // Backend will handle response encryption automatically when enabled
    return false;
  }

  protected static extractApiErrorMessage(
    error: any,
    fallback = "An error occurred"
  ) {
    if (typeof error === "string") {
      try {
        const parsed = JSON.parse(error);
        if (parsed?.message && typeof parsed.message === "string") {
          return parsed.message;
        }
        if (parsed?.error && typeof parsed.error === "string") {
          return parsed.error;
        }
      } catch {
        if (error.trim()) return error;
      }
    }

    let err = error;
    if (typeof err === "string") {
      try {
        err = JSON.parse(err);
      } catch {
        return err.trim() || fallback;
      }
    }

    if (err?.response?.data?.message) {
      return err.response.data.message;
    }
    if (err?.message && typeof err.message === "string") {
      return err.message;
    }
    return fallback;
  }

  /**
   * Private method to handle authentication error responses
   * @param response - Fetch response object
   * @param operation - Operation name for logging
   */
  private static shouldClearSessionOnAuthFailure(response: Response): boolean {
    if (!responseAuthToken.has(response)) {
      return false;
    }
    const used = responseAuthToken.get(response) ?? null;
    const current = localStorage.getItem("token");
    if (!used || !current) {
      return false;
    }
    return used === current;
  }

  private static createAuthSessionError(message: string): Error {
    const err = new Error(message);
    err.name = AUTH_SESSION_ERROR;
    return err;
  }

  /**
   * On 401 with the current JWT, clear session once and redirect on protected routes.
   * Skipped during post-login grace period so stale in-flight requests do not log the user out.
   */
  private static clearSessionOnAuthFailure(_response: Response): void {
    if (isWithinPostLoginGracePeriod()) {
      console.warn(
        "401 during post-login grace period — session kept (stale in-flight request likely)"
      );
      return;
    }
    if (!this.shouldClearSessionOnAuthFailure(_response)) {
      console.warn("401 on non-current token — session kept");
      return;
    }
    invalidateAuthSession(
      "Your session is no longer valid. Please sign in again."
    );
  }

  private static handleError(response: Response, operation: string): void {
    if (response.status === 401) {
      this.clearSessionOnAuthFailure(response);
      throw this.createAuthSessionError(
        "Session expired. Please login again."
      );
    }

    if (response.status === 403) {
      throw new Error("You do not have permission to access this resource");
    }
  }

  /**
   * Private method to handle authentication error responses
   * @param response - Fetch response object
   * @param operation - Operation name for logging
   */
  private static handleAuthError(response: Response, operation: string): void {
    if (response.status === 401) {
      this.clearSessionOnAuthFailure(response);
      throw this.createAuthSessionError(
        "Session expired. Please login again."
      );
    }

    if (response.status === 403) {
      throw new Error("You do not have permission to access this resource");
    }
  }

  /**
   * Private method to process API response
   * @param response - Fetch response object
   * @param operation - Operation name for logging
   * @returns Parsed API response
   */
  private static async processResponse(
    response: Response,
    operation: string
  ): Promise<ApiResponse<any>> {
    if (!response.ok) {
      // Unauthenticated calls (e.g. login): 401 means invalid credentials — do not
      // redirect like session expiry (that behavior belongs in processAuthenticatedResponse).
      const errorText = await response.text();
      console.error("Request failed with status:", response.status);
      console.error("Error response:", errorText);
      const msg = this.extractApiErrorMessage(
        errorText || `Request failed with status ${response.status}`,
        `Request failed with status ${response.status}`
      );
      throw new Error(msg);
    }

    let result = await response.json();

    // Check if response is encrypted and decrypt if needed
    const isEncrypted =
      response.headers.get("x-encrypted") === "true" ||
      encryptionService.isEncryptedResponse(result);

    console.log("🔍 Response processing debug:", {
      operation,
      hasXEncryptedHeader: response.headers.get("x-encrypted") === "true",
      isEncryptedResponse: encryptionService.isEncryptedResponse(result),
      isEncryptionEnabled: encryptionService.isEnabled(),
      resultKeys: Object.keys(result || {}),
      resultType: typeof result,
    });

    if (isEncrypted && encryptionService.isEnabled()) {
      console.log("🔐 Decrypting encrypted response for:", operation);
      try {
        const decryptedData = encryptionService.decryptResponse(result);
        result = decryptedData;
        console.log("✅ Response decrypted successfully for:", operation);
      } catch (error) {
        console.error("❌ Failed to decrypt response for:", operation, error);
        // Continue with original response if decryption fails
      }
    } else {
      console.log("⚠️ Response not decrypted:", {
        isEncrypted,
        encryptionEnabled: encryptionService.isEnabled(),
        reason: !isEncrypted ? "not encrypted" : "encryption disabled",
      });
    }

    if (result.status === "success") {
      this.logOperation(`✅ ${operation} successful`);
      return result;
    } else {
      throw new Error(
        this.extractApiErrorMessage(result.message || "Request failed")
      );
    }
  }

  /**
   * Private method to process authenticated API response
   * @param response - Fetch response object
   * @param operation - Operation name for logging
   * @returns Parsed API response
   */
  private static async processAuthenticatedResponse(
    response: Response,
    operation: string
  ): Promise<ApiResponse<any>> {
    if (response.status === 401) {
      console.log("Authentication failed (401)");
      this.clearSessionOnAuthFailure(response);
      throw this.createAuthSessionError(
        "Session expired. Please login again."
      );
    }

    if (response.status === 403) {
      const errorText = await response.text();
      console.error("Request failed with status: 403");
      console.error("Error response:", errorText);
      let errorCode: string | undefined;
      let msg = "You do not have permission to access this resource";
      try {
        const errorBody = JSON.parse(errorText);
        msg = errorBody.message || msg;
        errorCode = errorBody.errorCode;
      } catch {}
      const err = new Error(msg);
      (err as any).errorCode = errorCode;
      throw err;
    }

    if (!response.ok) {
      this.handleAuthError(response, operation);

      // Read error text from response
      const errorText = await response.text();
      console.error("Request failed with status:", response.status);
      console.error("Error response:", errorText);
      throw new Error(
        this.extractApiErrorMessage(
          errorText || `Request failed with status ${response.status}`
        )
      );
    }

    let result = await response.json();

    // Check if response is encrypted and decrypt if needed
    const isEncrypted =
      response.headers.get("x-encrypted") === "true" ||
      encryptionService.isEncryptedResponse(result);

    if (isEncrypted && encryptionService.isEnabled()) {
      console.log("🔐 Decrypting encrypted response for:", operation);
      try {
        const decryptedData = encryptionService.decryptResponse(result);
        result = decryptedData;
        console.log("✅ Response decrypted successfully for:", operation);
      } catch (error) {
        console.error("❌ Failed to decrypt response for:", operation, error);
        // Continue with original response if decryption fails
      }
    }

    if (result.status === "success") {
      this.logOperation(`✅ ${operation} successful (Authenticated)`);
      return result;
    } else {
      throw new Error(
        this.extractApiErrorMessage(
          result.message || "Authenticated request failed"
        )
      );
    }
  }

  /**
   * Private method to handle authentication-specific errors
   * @param error - Error object
   * @param operation - Operation name for logging
   */
  private static handleAuthenticationError(
    error: any,
    operation: string
  ): void {
    this.logError(`${operation} (Authenticated)`, error);

    if (error instanceof Error) {
      if (
        error.message.includes("Session expired") ||
        error.message.includes("No authentication token")
      ) {
        this.logError(
          operation,
          "Authentication required - redirecting to login"
        );
      }
    }
  }

  /**
   * Private method to make authenticated fetch request
   * @param url - URL to make request to
   * @param options - Fetch options
   * @returns Fetch response
   */
  private static async fetchWithAuth(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const fullUrl = this.ensureFullUrl(url);
    const token = localStorage.getItem("token");
    const headers = this.getAuthHeaders();

    console.log("Making authenticated request to:", fullUrl);
    console.log("Request options:", options);
    console.log("Auth headers:", headers);

    const response = await fetch(fullUrl, {
      ...options,
      credentials: "include",
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    responseAuthToken.set(response, token);

    console.log("Response status:", response.status);
    console.log(
      "Response headers:",
      Object.fromEntries(response.headers.entries())
    );

    return response;
  }

  private static async fetchWithAuthMultipart(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const fullUrl = this.ensureFullUrl(url);
    const token = localStorage.getItem("token");
    const headers = this.getAuthHeadersMultipart();
    const response = await fetch(fullUrl, {
      ...options,
      credentials: "include",
      headers: {
        ...headers,
        ...(options.headers as Record<string, string>),
      },
    });
    responseAuthToken.set(response, token);
    return response;
  }

  // ==================== PUBLIC API METHODS (No Authentication) ====================

  /**
   * Makes a GET request without authentication
   * @param url - URL to make request to
   * @param operation - Operation name for logging
   * @returns Promise resolving to API response
   */
  protected static async get(
    url: string,
    operation: string
  ): Promise<ApiResponse<any>> {
    try {
      this.logOperation(`🔍 ${operation}`);
      const fullUrl = this.ensureFullUrl(url);
      this.logOperation("📡 API Request URL", fullUrl);

      const response = await fetch(fullUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      return this.processResponse(response, operation);
    } catch (error) {
      this.logError(operation, error);
      throw new Error(this.extractApiErrorMessage(error));
    }
  }

  /**
   * Makes a GET request with query parameters (no authentication)
   * @param url - Base URL
   * @param filters - Filters to convert to query parameters
   * @param operation - Operation name for logging
   * @returns Promise resolving to API response
   */
  protected static async getWithQuery(
    url: string,
    filters: Record<string, any>,
    operation: string
  ): Promise<ApiResponse<any>> {
    const queryParams = this.buildQueryParams(filters);
    const fullUrl = `${this.ensureFullUrl(url)}?${queryParams.toString()}`;

    this.logOperation(`🔍 ${operation}`, filters);
    return this.get(fullUrl, operation);
  }

  /**
   * Makes a POST request without authentication
   * @param url - URL to make request to
   * @param data - Data to send in request body
   * @param operation - Operation name for logging
   * @param customHeaders - Optional custom headers to include
   * @returns Promise resolving to API response
   */
  protected static async post(
    url: string,
    data: any,
    operation: string,
    customHeaders: Record<string, string> = {}
  ): Promise<ApiResponse<any>> {
    try {
      // Sanitize data to ensure no moment objects are serialized
      const sanitizedData = this.sanitizeForJson(data);
      this.logOperation(`📝 ${operation}`, sanitizedData);
      const fullUrl = this.ensureFullUrl(url);
      this.logOperation("📡 API Request URL", fullUrl);

      // Check if request should be encrypted
      let requestData = sanitizedData;
      let headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...customHeaders,
      };

      if (this.shouldEncryptRequest(url)) {
        console.log("🔐 Encrypting request for:", operation);
        const encryptedRequest = encryptionService.createEncryptedRequest(requestData);
        requestData = encryptedRequest;
        headers["X-Encrypted"] = "true";
      }

      const response = await fetch(fullUrl, {
        method: "POST",
        headers,
        body: JSON.stringify(requestData),
      });

      return this.processResponse(response, operation);
    } catch (error) {
      this.logError(operation, error);
      throw new Error(this.extractApiErrorMessage(error));
    }
  }

  // ==================== AUTHENTICATED API METHODS ====================

  /**
   * Makes an authenticated GET request
   * @param url - URL to make request to
   * @param operation - Operation name for logging
   * @returns Promise resolving to API response
   */
  protected static async getAuthenticated(
    url: string,
    operation: string
  ): Promise<ApiResponse<any>> {
    try {
      this.logOperation(`🔐 🔍 ${operation} (Authenticated)`);
      this.logOperation("📡 API Request URL", url);

      const response = await this.fetchWithAuth(url, { method: "GET" });
      return this.processAuthenticatedResponse(response, operation);
    } catch (error) {
      this.handleAuthenticationError(error, operation);
      throw error;
    }
  }

  /**
   * Makes an authenticated GET request with query parameters
   * @param url - Base URL
   * @param filters - Filters to convert to query parameters
   * @param operation - Operation name for logging
   * @returns Promise resolving to API response
   */
  protected static async getAuthenticatedWithQuery(
    url: string,
    filters: Record<string, any>,
    operation: string
  ): Promise<ApiResponse<any>> {
    const queryParams = this.buildQueryParams(filters);
    const fullUrl = `${url}?${queryParams.toString()}`;

    this.logOperation(`🔐 🔍 ${operation} (Authenticated)`, filters);
    return this.getAuthenticated(fullUrl, operation);
  }

  /**
   * Makes an authenticated POST request
   * @param url - URL to make request to
   * @param data - Data to send in request body
   * @param operation - Operation name for logging
   * @returns Promise resolving to API response
   */
  protected static async postAuthenticated(
    url: string,
    data: any,
    operation: string
  ): Promise<ApiResponse<any>> {
    try {
      this.logOperation(`🔐 📝 ${operation} (Authenticated)`, data);

      // Check if request should be encrypted
      let requestData = data;
      let headers: Record<string, string> = {};

      if (this.shouldEncryptRequest(url)) {
        console.log("🔐 Encrypting authenticated request for:", operation);
        const encryptedRequest = encryptionService.createEncryptedRequest(data);
        requestData = encryptedRequest;
        headers["X-Encrypted"] = "true";
      }

      const response = await this.fetchWithAuth(url, {
        method: "POST",
        headers,
        body: JSON.stringify(requestData),
      });
      return this.processAuthenticatedResponse(response, operation);
    } catch (error) {
      this.handleAuthenticationError(error, operation);
      const err = new Error(this.extractApiErrorMessage(error));
      (err as any).errorCode = (error as any)?.errorCode;
      throw err;
    }
  }

  /**
   * Authenticated POST with multipart body (e.g. file upload). Do not set Content-Type manually.
   */
  protected static async postMultipartAuthenticated(
    url: string,
    formData: FormData,
    operation: string
  ): Promise<ApiResponse<any>> {
    try {
      this.logOperation(`🔐 📎 ${operation} (Multipart)`, { url });
      const response = await this.fetchWithAuthMultipart(url, {
        method: "POST",
        body: formData,
      });
      return this.processAuthenticatedResponse(response, operation);
    } catch (error) {
      this.handleAuthenticationError(error, operation);
      const err = new Error(this.extractApiErrorMessage(error));
      (err as any).errorCode = (error as any)?.errorCode;
      throw err;
    }
  }

  /**
   * Makes an authenticated PUT request
   * @param url - URL to make request to
   * @param data - Data to send in request body
   * @param operation - Operation name for logging
   * @returns Promise resolving to API response
   */
  protected static async putAuthenticated(
    url: string,
    data: any,
    operation: string
  ): Promise<ApiResponse<any>> {
    try {
      // Sanitize data to ensure no moment objects are serialized
      const sanitizedData = this.sanitizeForJson(data);
      this.logOperation(`🔐 ✏️ ${operation} (Authenticated)`, sanitizedData);

      // Check if request should be encrypted
      let requestData = sanitizedData;
      let headers: Record<string, string> = {};

      if (this.shouldEncryptRequest(url)) {
        console.log("🔐 Encrypting authenticated request for:", operation);
        const encryptedRequest = encryptionService.createEncryptedRequest(requestData);
        requestData = encryptedRequest;
        headers["X-Encrypted"] = "true";
      }

      const response = await this.fetchWithAuth(url, {
        method: "PUT",
        headers,
        body: JSON.stringify(requestData),
      });
      return this.processAuthenticatedResponse(response, operation);
    } catch (error) {
      this.handleAuthenticationError(error, operation);
      const err = new Error(this.extractApiErrorMessage(error));
      (err as any).errorCode = (error as any)?.errorCode;
      throw err;
    }
  }
  
  /**
   * Sanitize data object to convert moment objects to date strings before JSON serialization
   * This prevents moment objects from being serialized as ISO datetime strings
   */
  private static sanitizeForJson(obj: any): any {
    if (!obj) return obj;
    
    // Check if it's a moment object by checking for moment's internal property
    // Moment objects have _isAMomentObject property set to true
    if (typeof obj === 'object' && obj !== null && obj._isAMomentObject === true) {
      try {
        // Try to use moment's methods if available
        if (typeof obj.isValid === 'function' && obj.isValid()) {
          // Format as YYYY-MM-DD using local date components
          // Use local() to ensure we're working with local time, not UTC
          const localMoment = obj.local ? obj.local() : obj;
          const year = localMoment.year();
          const month = localMoment.month() + 1; // moment months are 0-indexed
          const day = localMoment.date();
          return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
      } catch (e) {
        console.warn('Error sanitizing moment object:', e);
      }
    }
    
    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeForJson(item));
    }
    
    // Handle objects (but skip Date objects as they're handled by JSON.stringify)
    if (typeof obj === 'object' && obj !== null && !(obj instanceof Date)) {
      const sanitized: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          sanitized[key] = this.sanitizeForJson(obj[key]);
        }
      }
      return sanitized;
    }
    
    return obj;
  }

  /**
   * Makes an authenticated DELETE request
   * @param url - URL to make request to
   * @param operation - Operation name for logging
   * @returns Promise resolving to API response
   */
  protected static async deleteAuthenticated(
    url: string,
    operation: string
  ): Promise<ApiResponse<any>> {
    try {
      this.logOperation(`🔐 🗑️ ${operation} (Authenticated)`);

      const response = await this.fetchWithAuth(url, { method: "DELETE" });
      return this.processAuthenticatedResponse(response, operation);
    } catch (error) {
      this.handleAuthenticationError(error, operation);
      const err = new Error(this.extractApiErrorMessage(error));
      (err as any).errorCode = (error as any)?.errorCode;
      throw err;
    }
  }
}
